import { useRef, useLayoutEffect } from 'react'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { prefersReducedMotion, isMobile, getParallaxSpeed } from './animation-config'

gsap.registerPlugin(ScrollTrigger)

interface ParallaxLayer {
  /** CSS selector for the layer */
  selector: string
  /** Speed multiplier (0.1 = slow, 1 = normal, 2 = fast) */
  speed: number
}

interface ParallaxOptions {
  /** Start position for ScrollTrigger */
  start?: string
  /** End position for ScrollTrigger */
  end?: string
}

/**
 * Multi-layer parallax effect - Creative Bold style
 * Creates depth by moving layers at different speeds during scroll
 *
 * @example
 * const sectionRef = useParallaxLayers({
 *   background: { selector: '.bg-shapes', speed: 0.1 },
 *   midground: { selector: '.floating-elements', speed: 0.3 },
 *   foreground: { selector: '.content', speed: 0.5 }
 * })
 */
export function useParallaxLayers<T extends HTMLElement>(
  layers: {
    background?: ParallaxLayer
    midground?: ParallaxLayer
    foreground?: ParallaxLayer
  },
  options?: ParallaxOptions
) {
  const ref = useRef<T>(null)
  // Destructure to primitives so an inline `layers` object literal doesn't rebuild
  // every ScrollTrigger on each render (and so the effect deps stay primitive).
  const { background, midground, foreground } = layers
  const bgSelector = background?.selector
  const bgSpeed = background?.speed
  const midSelector = midground?.selector
  const midSpeed = midground?.speed
  const fgSelector = foreground?.selector
  const fgSpeed = foreground?.speed
  const start = options?.start
  const end = options?.end

  useLayoutEffect(() => {
    if (!ref.current || prefersReducedMotion()) return

    const ctx = gsap.context(() => {
      const allLayers = [
        { selector: bgSelector, speed: bgSpeed },
        { selector: midSelector, speed: midSpeed },
        { selector: fgSelector, speed: fgSpeed },
      ].filter((l): l is { selector: string; speed: number } => l.selector != null && l.speed != null)

      allLayers.forEach((layer) => {
        const elements = ref.current!.querySelectorAll(layer.selector)
        if (elements.length === 0) return

        // Adjust speed for mobile
        const adjustedSpeed = getParallaxSpeed(layer.speed)

        // Calculate movement based on speed
        // Lower speed = element moves less = appears further away
        // Higher speed = element moves more = appears closer
        const yPercent = (1 - adjustedSpeed) * 50

        gsap.to(elements, {
          yPercent,
          ease: 'none',
          scrollTrigger: {
            trigger: ref.current,
            start: start ?? 'top bottom',
            end: end ?? 'bottom top',
            scrub: true,
          },
        })
      })
    }, ref)

    return () => ctx.revert()
  }, [bgSelector, bgSpeed, midSelector, midSpeed, fgSelector, fgSpeed, start, end])

  return ref
}

/**
 * Simple parallax for a single element
 */
export function useParallax<T extends HTMLElement>(speed = 0.5, options?: ParallaxOptions) {
  const ref = useRef<T>(null)

  useLayoutEffect(() => {
    if (!ref.current || prefersReducedMotion()) return

    const adjustedSpeed = getParallaxSpeed(speed)
    const yPercent = (1 - adjustedSpeed) * 30

    const ctx = gsap.context(() => {
      gsap.to(ref.current, {
        yPercent,
        ease: 'none',
        scrollTrigger: {
          trigger: ref.current,
          start: options?.start ?? 'top bottom',
          end: options?.end ?? 'bottom top',
          scrub: true,
        },
      })
    }, ref)

    return () => ctx.revert()
  }, [speed, options?.start, options?.end])

  return ref
}

/**
 * Horizontal parallax - element moves sideways during scroll
 */
export function useHorizontalParallax<T extends HTMLElement>(
  speed = 0.3,
  direction: 'left' | 'right' = 'left',
  options?: ParallaxOptions
) {
  const ref = useRef<T>(null)

  useLayoutEffect(() => {
    if (!ref.current || prefersReducedMotion()) return

    const adjustedSpeed = isMobile() ? speed * 0.5 : speed
    const xPercent = direction === 'left' ? -adjustedSpeed * 20 : adjustedSpeed * 20

    const ctx = gsap.context(() => {
      gsap.to(ref.current, {
        xPercent,
        ease: 'none',
        scrollTrigger: {
          trigger: ref.current,
          start: options?.start ?? 'top bottom',
          end: options?.end ?? 'bottom top',
          scrub: true,
        },
      })
    }, ref)

    return () => ctx.revert()
  }, [speed, direction, options?.start, options?.end])

  return ref
}

/**
 * Scale parallax - element scales during scroll
 */
export function useScaleParallax<T extends HTMLElement>(startScale = 0.85, endScale = 1, options?: ParallaxOptions) {
  const ref = useRef<T>(null)

  useLayoutEffect(() => {
    if (!ref.current || prefersReducedMotion()) return

    const ctx = gsap.context(() => {
      gsap.fromTo(
        ref.current,
        { scale: startScale },
        {
          scale: endScale,
          ease: 'none',
          scrollTrigger: {
            trigger: ref.current,
            start: options?.start ?? 'top bottom',
            end: options?.end ?? 'bottom top',
            scrub: true,
          },
        }
      )
    }, ref)

    return () => ctx.revert()
  }, [startScale, endScale, options?.start, options?.end])

  return ref
}
